import { createFileRoute } from "@tanstack/react-router";
import React, { useId, useMemo, useState } from "react";
import { z } from "zod";
import {
  Plus,
  Clock,
  FileSignature,
  MapPin,
  Briefcase,
  DollarSign,
  AlertCircle,
  RefreshCw,
  Loader2,
  CalendarDays,
  Calendar,
  Users,
  ChevronDown,
  ChevronUp,
  XCircle,
  CheckCircle2,
  LayoutList,
  CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RouterOutputs, useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useOrganization } from "@/hooks/use-organization";
import { useRolePermissions, Permission } from "@/hooks/use-role-permissions";
import { WageFrequency, ScheduleType } from "@api/shared-runtime/enums";
import { OfferOrganizationWageSetLabel } from "@/features/job-offer/components/offer-organization-wage-set-label";
import { wageFrequencyToEnding } from "@/features/job-offer/utils/wage-frequency-to-ending";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ProfileModal } from "@/features/profile/components/ProfileModal";
import { formatDistanceToNow, isPast, parseISO } from "date-fns";
import { OrganizationJobOfferCalendar } from "@/features/job-offer/components/organization-job-offer-calendar";
import { OrganizationJobOfferWeeklyScheduler } from "@/features/job-offer/components/organization-job-offer-weekly-scheduler";
import { Views, type View } from "react-big-calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { JobOffer } from "@/features/job-offer/forms/job-offer";
import { JobOfferPreviewDialog } from "@/features/job-offer/components/job-offer-preview-dialog";
import type { JobOfferData } from "@/features/job-offer/utils/job-offer-data";
import { calculateDurationMinutes } from "@/features/job-offer/utils/job-offer-data";
import { useOrgPath } from "@/hooks/useOrgPath";

const viewModeOptions = [
  { value: "calendar", label: "Calendar", icon: Calendar },
  { value: "scheduler", label: "Scheduler", icon: CalendarClock },
  { value: "list", label: "List", icon: LayoutList },
] as const;

const calendarViewMap: Record<string, View> = {
  month: Views.MONTH,
  week: Views.WEEK,
  day: Views.DAY,
};

const calendarViewReverseMap: Record<View, string> = {
  [Views.MONTH]: "month",
  [Views.WEEK]: "week",
  [Views.DAY]: "day",
  [Views.AGENDA]: "month",
  [Views.WORK_WEEK]: "week",
};

const JobOfferPage = () => {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user } = Route.useRouteContext();
  const { selectedOrgBusinessUnit } = useOrganization();
  const { hasPermission } = useRolePermissions(user);
  const orgPath = useOrgPath();
  const filterId = useId();
  const [facilityFilter, setFacilityFilter] = useState<string>("all");
  const [showInactive, setShowInactive] = useState(false);
  const [profileModalUserId, setProfileModalUserId] = useState<string | null>(
    null,
  );
  const [expandedDeclines, setExpandedDeclines] = useState<string | null>(null);
  const [calendarDate, setCalendarDate] = React.useState<Date>(new Date());

  // Derive state from URL search params
  const viewMode = search.view ?? "calendar";
  const calendarView =
    calendarViewMap[search.calendarView ?? "month"] ?? Views.MONTH;
  // Update URL when view mode changes
  const setViewMode = React.useCallback(
    (newViewMode: "calendar" | "scheduler" | "list") => {
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, view: newViewMode }),
        replace: true,
      });
    },
    [navigate],
  );

  // Update URL when calendar view changes
  const setCalendarView = React.useCallback(
    (newCalendarView: View) => {
      navigate({
        to: ".",
        search: (prev) => ({
          ...prev,
          calendarView: calendarViewReverseMap[newCalendarView] ?? "month",
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDefaults, setCreateDefaults] = useState<
    JobOfferData["schedules"] | undefined
  >(undefined);
  const [selectedPreviewOfferId, setSelectedPreviewOfferId] = useState<
    string | null
  >(null);
  const createDialogKey = useMemo(
    () =>
      createDefaults
        ?.map((schedule) =>
          [
            schedule.startsAt,
            schedule.durationMinutes,
            schedule.rrule ?? "single",
          ].join("-"),
        )
        .join("|") ?? "new-offer",
    [createDefaults],
  );

  const {
    data: jobOffers,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    trpc.jobOffer.organizationGetAll.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
      },
      {
        enabled: !!selectedOrgBusinessUnit?.id,
      },
    ),
  );

  const { data: myPoolAccess } = useQuery(
    trpc.organization.getMyProviderPoolAccess.queryOptions(
      {
        organizationBusinessUnitId: selectedOrgBusinessUnit?.id,
      },
      {
        enabled: !!selectedOrgBusinessUnit?.id,
      },
    ),
  );

  const selectedPreviewOffer = useMemo(
    () => jobOffers?.find((o) => o.id === selectedPreviewOfferId) ?? null,
    [jobOffers, selectedPreviewOfferId],
  );

  // Open the preview dialog when arriving via a deep link (?offerId=...).
  React.useEffect(() => {
    if (search.offerId) {
      setSelectedPreviewOfferId(search.offerId);
    }
  }, [search.offerId]);

  // Strip ?offerId once the preview is dismissed so it doesn't reopen on
  // refresh/back, and the URL reflects the current view.
  const clearOfferIdParam = React.useCallback(() => {
    if (search.offerId) {
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, offerId: undefined }),
        replace: true,
      });
    }
  }, [navigate, search.offerId]);

  const closePreview = React.useCallback(() => {
    setSelectedPreviewOfferId(null);
    clearOfferIdParam();
  }, [clearOfferIdParam]);

  const facilities = useMemo(() => {
    if (!jobOffers) {
      return [];
    }
    const uniqueFacilities = new Map<string, { id: string; name: string }>();
    jobOffers.forEach((offer) => {
      if (offer.facility) {
        uniqueFacilities.set(offer.facility.id, {
          id: offer.facility.id,
          name: offer.facility.name || "Unnamed Facility",
        });
      }
    });
    return Array.from(uniqueFacilities.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [jobOffers]);

  const offerStatusMap = useMemo(() => {
    const map = new Map<string, OfferStatusFilter>();
    if (!jobOffers) {
      return map;
    }
    for (const offer of jobOffers) {
      map.set(offer.id, getOfferStatusType(offer));
    }
    return map;
  }, [jobOffers]);

  const filteredJobOffers = useMemo(() => {
    if (!jobOffers) {
      return [];
    }

    return jobOffers.filter((offer) => {
      if (facilityFilter !== "all" && offer.facility?.id !== facilityFilter) {
        return false;
      }

      const statusType =
        offerStatusMap.get(offer.id) ?? getOfferStatusType(offer);

      if (viewMode === "calendar" || viewMode === "scheduler") {
        // Calendar/scheduler: show active + awaiting + completed only
        return (
          statusType === "accepted" ||
          statusType === "pending" ||
          statusType === "completed"
        );
      }

      // List view: show active + awaiting by default, add inactive when toggled
      if (showInactive) {
        return true;
      }
      return statusType === "accepted" || statusType === "pending";
    });
  }, [jobOffers, facilityFilter, viewMode, showInactive, offerStatusMap]);

  const hasFilters = facilityFilter !== "all" || showInactive;
  const hasJobOffers = filteredJobOffers.length > 0;

  const closeCreateDialog = React.useCallback(() => {
    setIsCreateDialogOpen(false);
    setCreateDefaults(undefined);
  }, []);

  const handleCreateFromCalendar = React.useCallback(
    ({
      startDate,
      startTime,
      endTime,
    }: {
      startDate: string;
      endDate: string;
      startTime?: string;
      endTime?: string;
    }) => {
      // Convert old date range format to new schedule format
      const defaultStartTime = startTime ?? "09:00";
      // If end time not provided, default to 8 hours after start time
      const defaultEndTime =
        endTime ?? calculateDefaultEndTime(defaultStartTime);
      const durationMinutes = calculateDurationMinutes(
        defaultStartTime,
        defaultEndTime,
      );

      // Combine date and time into ISO datetime string
      const startsAt = `${startDate}T${defaultStartTime}:00`;

      setCreateDefaults([
        {
          startsAt,
          durationMinutes,
          rrule: null, // Single occurrence
          scheduleType: ScheduleType.Regular,
        },
      ]);
      setIsCreateDialogOpen(true);
    },
    [],
  );

  const handleEditJobOffer = React.useCallback(
    (jobOfferId: string) => {
      navigate({
        to: orgPath("/jobs/offer/edit/" + jobOfferId),
      });
    },
    [navigate, orgPath],
  );

  return (
    <div className="min-w-0 flex-1 overflow-y-auto overflow-x-auto max-w-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900">
            Job Offers
          </h1>
          {hasPermission(Permission.ManageJobOffers) && (
            <Button asChild className="w-full gap-2 font-semibold sm:w-auto">
              <Link to={orgPath("/jobs/offer/create")}>
                <Plus aria-hidden="true" className="size-4 shrink-0" />
                Create offer
              </Link>
            </Button>
          )}
        </div>

        {jobOffers && jobOffers.length > 0 ? (
          <div className="min-w-0 rounded-2xl border border-slate-200 bg-white shadow-xs">
            {/* Toolbar */}
            <div className="grid min-w-0 grid-cols-1 items-end gap-4 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,18rem)_auto]">
              {/* View Mode Switcher */}
              <fieldset className="min-w-0 sm:col-span-2 xl:col-span-1">
                <legend className="mb-2 text-sm font-medium leading-5 text-slate-700">
                  View
                </legend>
                <div className="grid h-12 grid-cols-3 rounded-xl border border-slate-200 bg-slate-100">
                  {viewModeOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <label
                        key={option.value}
                        htmlFor={`${filterId}-view-${option.value}`}
                        className="relative flex h-full min-w-0 cursor-pointer select-none p-1"
                      >
                        <input
                          id={`${filterId}-view-${option.value}`}
                          type="radio"
                          name={`${filterId}-view`}
                          value={option.value}
                          checked={viewMode === option.value}
                          onChange={() => setViewMode(option.value)}
                          className="peer sr-only"
                        />
                        <span className="flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-1 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 peer-checked:bg-white peer-checked:text-primary peer-checked:shadow-xs peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-slate-100">
                          <Icon
                            aria-hidden="true"
                            className="hidden size-4 shrink-0 sm:block"
                          />
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              {/* Facility Filter */}
              {facilities.length > 0 && (
                <div className="grid min-w-0 gap-2">
                  <Label htmlFor={`${filterId}-facility`}>Facility</Label>
                  <Select
                    value={facilityFilter}
                    onValueChange={(value) => setFacilityFilter(value)}
                  >
                    <SelectTrigger
                      id={`${filterId}-facility`}
                      className="min-w-0 text-left [&>span]:min-w-0 [&>span]:truncate"
                    >
                      <SelectValue placeholder="All facilities" />
                    </SelectTrigger>
                    <SelectContent className="max-w-[calc(100vw-2rem)]">
                      <SelectItem value="all">All Facilities</SelectItem>
                      {facilities.map((facility) => (
                        <SelectItem
                          key={facility.id}
                          value={facility.id}
                          className="whitespace-normal [overflow-wrap:anywhere]"
                        >
                          {facility.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Show inactive toggle - list view only */}
              {viewMode === "list" && (
                <div className="flex min-h-12 items-center gap-3">
                  <Switch
                    id={`${filterId}-show-inactive`}
                    checked={showInactive}
                    onCheckedChange={setShowInactive}
                  />
                  <Label
                    htmlFor={`${filterId}-show-inactive`}
                    className="cursor-pointer"
                  >
                    Show inactive
                  </Label>
                </div>
              )}
            </div>

            {/* Active Filters Bar */}
            {hasFilters && (
              <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-b-2xl border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Filters:
                </span>
                {showInactive && (
                  <Badge
                    variant="secondary"
                    className="border-primary/15 bg-primary/5 text-primary hover:bg-primary/10"
                  >
                    Showing inactive
                  </Badge>
                )}
                {facilityFilter !== "all" && (
                  <Badge
                    variant="secondary"
                    className="max-w-full whitespace-normal border-primary/15 bg-primary/5 text-primary [overflow-wrap:anywhere] hover:bg-primary/10"
                  >
                    {facilities.find(
                      (facility) => facility.id === facilityFilter,
                    )?.name ?? "Unknown"}
                  </Badge>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFacilityFilter("all");
                    setShowInactive(false);
                  }}
                  className="gap-1 text-primary hover:bg-primary/5 hover:text-primary"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Clear
                </Button>
              </div>
            )}
          </div>
        ) : null}

        <div className="space-y-4 sm:space-y-6">
          {isLoading ? (
            <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
              <CardContent className="p-6 sm:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-primary/5 p-4 rounded-2xl mb-6">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Loading job offers
                </h3>
                <p className="text-slate-600 max-w-md">
                  Please wait while we fetch the latest job offers for your
                  organization.
                </p>
              </CardContent>
            </Card>
          ) : isError ? (
            <Card className="rounded-2xl border-red-200 bg-red-50 shadow-xs">
              <CardContent className="p-6 sm:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-red-100 p-4 rounded-full mb-6">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  Failed to load job offers
                </h3>
                <p className="text-slate-600 mb-6 max-w-md">
                  {error instanceof Error
                    ? error.message
                    : "An error occurred while loading job offers. Please try again."}
                </p>
                <Button className="font-semibold" onClick={() => refetch()}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          ) : viewMode === "calendar" ? (
            <>
              <OrganizationJobOfferCalendar
                jobOffers={filteredJobOffers}
                view={calendarView}
                onViewChange={(nextView) => setCalendarView(nextView)}
                date={calendarDate}
                onNavigate={(nextDate) => setCalendarDate(nextDate)}
                onCreateFromSelection={handleCreateFromCalendar}
                onRequestEdit={handleEditJobOffer}
                onCanceled={() => refetch()}
                user={user}
              />
              <p className="text-xs text-slate-500">
                Tip: Click a day or drag across the calendar to propose a
                schedule, or hover any day in month view to reveal the quick add
                button.
              </p>
              {!hasJobOffers && (
                <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
                  <CardContent className="p-6 sm:p-10 text-center space-y-3">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-primary/5">
                      <Calendar className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      No job offers scheduled
                    </h3>
                    <p className="text-sm text-slate-600">
                      Use the calendar above to pick a date and start a new job
                      offer.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          ) : viewMode === "scheduler" ? (
            <OrganizationJobOfferWeeklyScheduler
              jobOffers={filteredJobOffers}
              date={calendarDate}
              onNavigate={(nextDate) => setCalendarDate(nextDate)}
              onCreateFromSelection={handleCreateFromCalendar}
              onRequestEdit={handleEditJobOffer}
              onCanceled={() => refetch()}
              user={user}
            />
          ) : hasJobOffers ? (
            filteredJobOffers.map((offer) => {
              const statusInfo = getOfferStatusDisplay(
                offer,
                offerStatusMap.get(offer.id),
              );
              const directRecipients = offer.userShares?.length ?? 0;
              const poolRecipients = offer.providerPoolShares?.length ?? 0;
              const totalRecipients = directRecipients + poolRecipients;
              const declines =
                offer.decisions?.filter(
                  (decision) => decision.providerDecision === false,
                ) ?? [];
              const updatedLabel = offer.updatedAt
                ? formatDistanceToNow(new Date(offer.updatedAt), {
                    addSuffix: true,
                  })
                : formatDistanceToNow(new Date(offer.createdAt), {
                    addSuffix: true,
                  });

              const statusBorderClass =
                offerStatusBorderClasses[statusInfo.type];

              return (
                <Card
                  key={offer.id}
                  className={`min-w-0 rounded-2xl border border-slate-200 border-l-4 ${statusBorderClass} bg-white shadow-xs overflow-hidden`}
                >
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="space-y-2 min-w-0 flex-1">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <button
                              type="button"
                              className="min-w-0 w-full rounded-sm text-left text-lg font-semibold leading-6 text-slate-900 [overflow-wrap:anywhere] transition-colors hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:w-auto sm:flex-1"
                              onClick={() =>
                                setSelectedPreviewOfferId(offer.id)
                              }
                            >
                              {offer.title || "Job offer"}
                            </button>
                            <Badge
                              className={`${statusInfo.badgeClass} border-0 text-xs font-medium shrink-0`}
                            >
                              {statusInfo.label}
                            </Badge>
                            {offer.proposals?.some(
                              (p) => p.status === "pending",
                            ) && (
                              <Badge className="bg-amber-100 text-amber-700 border-0 text-xs font-medium shrink-0">
                                Pending proposal
                              </Badge>
                            )}
                          </div>
                          {statusInfo.detailText && (
                            <p
                              className={`text-sm [overflow-wrap:anywhere] ${statusInfo.detailTone}`}
                            >
                              {statusInfo.detailText}
                              {statusInfo.detailActionLabel && (
                                <Button
                                  variant="link"
                                  className="ml-1 h-auto max-w-full whitespace-normal p-0 text-left text-sm [overflow-wrap:anywhere]"
                                  onClick={() =>
                                    setProfileModalUserId(
                                      statusInfo.detailActionUserId ?? null,
                                    )
                                  }
                                >
                                  {statusInfo.detailActionLabel}
                                </Button>
                              )}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                            {offer.facility?.name && (
                              <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 [overflow-wrap:anywhere]">
                                <Briefcase className="h-3.5 w-3.5 shrink-0" />
                                {offer.facility.name}
                              </span>
                            )}
                            {offer.facility?.city && (
                              <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 [overflow-wrap:anywhere]">
                                <MapPin className="h-3.5 w-3.5 shrink-0" />
                                {[offer.facility.city, offer.facility.state]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1.5 text-slate-500">
                              <Clock className="h-3.5 w-3.5 shrink-0" />
                              {updatedLabel}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Info Grid */}
                    <div className="grid min-w-0 grid-cols-2 lg:grid-cols-4 border-t border-slate-200 bg-slate-50/50">
                      <div className="min-w-0 p-3 sm:p-4 border-r border-slate-200">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="h-4 w-4 shrink-0 text-primary" />
                          <span className="text-xs font-medium uppercase text-slate-500">
                            Pay
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-700 [overflow-wrap:anywhere]">
                          {offer.regularWage ? `$${offer.regularWage}` : "—"}
                          {offer.wageFrequencyId && (
                            <span className="text-slate-500 font-normal">
                              {
                                wageFrequencyToEnding[
                                  offer.wageFrequencyId as WageFrequency
                                ]
                              }
                            </span>
                          )}
                        </p>
                        <OfferOrganizationWageSetLabel
                          organizationWageSetName={
                            offer.organizationWageSetName
                          }
                          className="mt-0.5 text-xs text-slate-500"
                        />
                      </div>
                      <div className="min-w-0 p-3 sm:p-4 border-slate-200 lg:border-r">
                        <div className="flex items-center gap-2 mb-1">
                          <CalendarDays className="h-4 w-4 shrink-0 text-primary" />
                          <span className="text-xs font-medium uppercase text-slate-500">
                            Schedule
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-700 [overflow-wrap:anywhere]">
                          {formatScheduleShort(offer)}
                        </p>
                      </div>
                      <div className="min-w-0 p-3 sm:p-4 border-r border-slate-200 border-t lg:border-t-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="h-4 w-4 shrink-0 text-primary" />
                          <span className="text-xs font-medium uppercase text-slate-500">
                            Recipients
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-700">
                          {totalRecipients > 0 ? (
                            <>
                              {totalRecipients}
                              <span className="text-slate-500 font-normal ml-1">
                                ({directRecipients} direct, {poolRecipients}{" "}
                                pool{poolRecipients !== 1 ? "s" : ""})
                              </span>
                            </>
                          ) : (
                            <span className="text-slate-500">Not shared</span>
                          )}
                        </p>
                      </div>
                      <div className="min-w-0 p-3 sm:p-4 border-t border-slate-200 lg:border-t-0">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
                          <span className="text-xs font-medium uppercase text-slate-500">
                            Responses
                          </span>
                        </div>
                        <p className="text-sm font-medium text-slate-700">
                          {declines.length > 0 ? (
                            <span className="text-rose-600">
                              {declines.length} declined
                            </span>
                          ) : (
                            <span className="text-slate-500">No responses</span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Declines Section */}
                    {declines.length > 0 && (
                      <Collapsible
                        open={expandedDeclines === offer.id}
                        onOpenChange={() =>
                          setExpandedDeclines((prev) =>
                            prev === offer.id ? null : offer.id,
                          )
                        }
                      >
                        <div className="border-t border-slate-200">
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm text-rose-600 transition-colors hover:bg-rose-50/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:px-5"
                            >
                              <span className="font-medium">
                                {declines.length} provider
                                {declines.length !== 1 ? "s" : ""} declined this
                                offer
                              </span>
                              {expandedDeclines === offer.id ? (
                                <ChevronUp className="h-4 w-4 shrink-0" />
                              ) : (
                                <ChevronDown className="h-4 w-4 shrink-0" />
                              )}
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="px-4 pb-4 space-y-2 sm:px-5">
                              {declines.map((decline) => {
                                const name =
                                  `${decline.firstName ?? ""} ${decline.lastName ?? ""}`.trim();
                                return (
                                  <div
                                    key={decline.id}
                                    className="flex min-w-0 flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
                                  >
                                    <Avatar className="h-8 w-8 shrink-0">
                                      <AvatarFallback className="bg-rose-100 text-rose-600 text-xs font-semibold">
                                        {getInitials(name || "Provider")}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0 flex-1 text-sm font-medium text-slate-700 [overflow-wrap:anywhere]">
                                      {name || "Provider"}
                                    </div>
                                    <Button
                                      variant="ghost"
                                      className="w-full text-primary hover:bg-primary/5 hover:text-primary sm:w-auto"
                                      onClick={() =>
                                        setProfileModalUserId(decline.userId)
                                      }
                                    >
                                      View profile
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="rounded-2xl border border-slate-200 bg-white shadow-xs">
              <CardContent className="p-6 sm:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-primary/5 p-4 rounded-2xl mb-6">
                  <FileSignature className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  No job offers yet
                </h3>
                <p className="text-slate-600 mb-6 max-w-md">
                  {hasPermission(Permission.ManageJobOffers)
                    ? myPoolAccess?.restricted
                      ? "Create your first job offer for providers in your assigned pools."
                      : "Create your first job offer to start targeting qualified providers with tailored opportunities."
                    : myPoolAccess?.restricted
                      ? "No providers in your assigned pools have active job offers yet."
                      : "No job offers have been created yet. Contact an admin to create job offers."}
                </p>
                {hasPermission(Permission.ManageJobOffers) && (
                  <Button
                    asChild
                    className="h-auto min-h-12 w-full gap-2 whitespace-normal text-center font-semibold sm:w-auto"
                  >
                    <Link to={orgPath("/jobs/offer/create")}>
                      <Plus aria-hidden="true" className="size-4 shrink-0" />
                      Create your first job offer
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <Dialog
          open={isCreateDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              closeCreateDialog();
            }
          }}
        >
          <DialogContent className="max-w-5xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border-slate-200 bg-white text-slate-900 sm:rounded-2xl">
            <DialogHeader>
              <DialogTitle>Create Job Offer</DialogTitle>
            </DialogHeader>
            <JobOffer
              key={createDialogKey}
              variant="dialog"
              defaultSchedules={createDefaults}
              onClose={closeCreateDialog}
              onCompleted={closeCreateDialog}
            />
          </DialogContent>
        </Dialog>

        {profileModalUserId && (
          <ProfileModal
            userId={profileModalUserId}
            onClose={() => setProfileModalUserId(null)}
            organizationBusinessUnitId={selectedOrgBusinessUnit.id}
          />
        )}

        <JobOfferPreviewDialog
          jobOffer={selectedPreviewOffer}
          open={!!selectedPreviewOfferId}
          onOpenChange={(open) => {
            if (!open) {
              closePreview();
            }
          }}
          onRequestEdit={(jobOfferId) => {
            navigate({
              to: orgPath("/jobs/offer/edit/" + jobOfferId),
            });
            closePreview();
          }}
          onCanceled={() => {
            refetch();
            closePreview();
          }}
          user={user}
        />
      </div>
    </div>
  );
};

const viewModeSchema = z
  .enum(["calendar", "scheduler", "list"])
  .catch("calendar");
const calendarViewSchema = z.enum(["month", "week", "day"]).catch("month");
export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/jobs/offer/",
)({
  component: JobOfferPage,
  validateSearch: z.object({
    view: viewModeSchema.optional(),
    calendarView: calendarViewSchema.optional(),
    // Deep-link target: when present, open that offer's preview dialog on mount
    offerId: z.string().uuid().optional(),
  }),
});

type OfferStatusFilter =
  | "all"
  | "pending"
  | "accepted"
  | "canceled"
  | "completed";

const offerStatusBorderClasses: Record<OfferStatusFilter, string> = {
  all: "border-l-primary",
  pending: "border-l-primary",
  accepted: "border-l-emerald-500",
  canceled: "border-l-rose-400",
  completed: "border-l-slate-300",
};

type OfferStatusDisplay = {
  type: OfferStatusFilter;
  label: string;
  badgeClass: string;
  dotClass: string;
  detailText?: string;
  detailTone?: string;
  detailActionLabel?: string;
  detailActionUserId?: string;
};
type OrganizationJobOffer =
  RouterOutputs["jobOffer"]["organizationGetAll"][number];
type OrganizationJobOfferDecision = NonNullable<
  NonNullable<OrganizationJobOffer["decisions"]>[number]
>;

function getOfferStatusType(offer: OrganizationJobOffer): OfferStatusFilter {
  if (offer.canceledByOrganizationBusinessUnitAt) {
    return "canceled";
  }

  const acceptedDecision = offer.decisions?.find(
    (decision: OrganizationJobOfferDecision) =>
      decision.providerDecision === true,
  );

  if (acceptedDecision?.providerCanceledAt) {
    return "canceled";
  }

  if (acceptedDecision) {
    // Check if all shifts are in the past (completed)
    const shifts = offer.shifts as
      | { endsAt: Date | string; status?: string }[]
      | undefined;
    if (shifts && shifts.length > 0) {
      const activeShifts = shifts.filter((s) => s.status !== "canceled");
      if (
        activeShifts.length > 0 &&
        activeShifts.every((s) =>
          isPast(typeof s.endsAt === "string" ? parseISO(s.endsAt) : s.endsAt),
        )
      ) {
        return "completed";
      }
    }
    return "accepted";
  }

  return "pending";
}

function getOfferStatusDisplay(
  offer: OrganizationJobOffer,
  precomputedStatus?: OfferStatusFilter,
): OfferStatusDisplay {
  if (offer.canceledByOrganizationBusinessUnitAt) {
    return {
      type: "canceled",
      label: "Canceled",
      badgeClass: "bg-rose-100 text-rose-700",
      dotClass: "bg-rose-500",
      detailText: "Canceled by organization",
      detailTone: "text-rose-600",
    };
  }

  const acceptedDecision = offer.decisions?.find(
    (decision: OrganizationJobOfferDecision) =>
      decision.providerDecision === true,
  );

  if (acceptedDecision?.providerCanceledAt) {
    const name =
      `${acceptedDecision.firstName ?? ""} ${acceptedDecision.lastName ?? ""}`.trim();
    return {
      type: "canceled",
      label: "Provider canceled",
      badgeClass: "bg-amber-100 text-amber-700",
      dotClass: "bg-amber-500",
      detailText: "Canceled by",
      detailTone: "text-amber-700",
      detailActionLabel: name || "Provider",
      detailActionUserId: acceptedDecision.userId,
    };
  }

  if (acceptedDecision) {
    const name =
      `${acceptedDecision.firstName ?? ""} ${acceptedDecision.lastName ?? ""}`.trim();
    const statusType = precomputedStatus ?? getOfferStatusType(offer);
    if (statusType === "completed") {
      return {
        type: "completed",
        label: "Completed",
        badgeClass: "bg-slate-100 text-slate-600",
        dotClass: "bg-slate-400",
        detailText: "Completed with",
        detailTone: "text-slate-600",
        detailActionLabel: name || "Provider",
        detailActionUserId: acceptedDecision.userId,
      };
    }
    return {
      type: "accepted",
      label: "Accepted",
      badgeClass: "bg-emerald-100 text-emerald-700",
      dotClass: "bg-emerald-500",
      detailText: "Accepted by",
      detailTone: "text-emerald-700",
      detailActionLabel: name || "Provider",
      detailActionUserId: acceptedDecision.userId,
    };
  }

  return {
    type: "pending",
    label: "Awaiting response",
    badgeClass: "bg-primary/10 text-primary",
    dotClass: "bg-primary",
    detailText: "Waiting for provider decision",
    detailTone: "text-primary",
  };
}

function formatScheduleShort(offer: OrganizationJobOffer) {
  if (!offer.shifts || offer.shifts.length === 0) {
    return "Not set";
  }

  const [first, ...rest] = offer.shifts;
  const startDate =
    typeof first.startsAt === "string"
      ? parseISO(first.startsAt)
      : first.startsAt;
  const endDate =
    typeof first.endsAt === "string" ? parseISO(first.endsAt) : first.endsAt;

  const start = formatDate(startDate);
  const end =
    startDate.toDateString() !== endDate.toDateString()
      ? formatDate(endDate)
      : null;

  let summary = end ? `${start} – ${end}` : start;
  if (rest.length > 0) {
    summary = `${summary} +${rest.length}`;
  }

  return summary;
}

function formatDate(value: Date | string) {
  const date = typeof value === "string" ? parseISO(value) : value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function getInitials(name: string) {
  const parts = name
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * Calculate end time that is 8 hours after start time.
 * Handles overnight shifts (wraps around midnight).
 */
function calculateDefaultEndTime(startTime: string): string {
  const [hours, minutes] = startTime.split(":").map(Number);
  const endHours = (hours + 8) % 24;
  return `${String(endHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
